import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 100%);
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const Title = styled.h1`
  color: #fff;
  font-size: 28px;
  margin: 0;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
`;

const Tab = styled.button`
  padding: 12px 24px;
  background: ${props => props.active ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'rgba(255,255,255,0.1)'};
  border: 1px solid ${props => props.active ? 'transparent' : 'rgba(255,255,255,0.2)'};
  color: #fff;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;

  &:hover {
    background: ${props => props.active ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'rgba(255,255,255,0.2)'};
  }
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  color: rgba(255,255,255,0.7);
  margin-bottom: 8px;
  font-size: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 8px;
  color: #fff;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white'%3e%3cpath d='M7 10l5 5 5-5z'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 20px;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
  
  option {
    background: #1a1a2e;
    color: #fff;
    padding: 10px;
  }
`;

const Button = styled.button`
  padding: 12px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: #fff;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  color: #fff;
`;

const Th = styled.th`
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid rgba(255,255,255,0.1);
  color: rgba(255,255,255,0.7);
  font-size: 14px;
`;

const Td = styled.td`
  padding: 12px;
  border-bottom: 1px solid rgba(255,255,255,0.05);
  font-size: 14px;
`;

const Badge = styled.span`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  background: ${props => {
    switch(props.type) {
      case 'day': return 'rgba(255,193,7,0.2)';
      case 'week': return 'rgba(0,193,255,0.2)';
      case 'season': return 'rgba(255,123,114,0.2)';
      case 'year': return 'rgba(40,167,69,0.2)';
      case 'permanent': return 'rgba(255,215,0,0.2)';
      default: return 'rgba(255,255,255,0.1)';
    }
  }};
  color: ${props => {
    switch(props.type) {
      case 'day': return '#ffc107';
      case 'week': return '#00c1ff';
      case 'season': return '#ff7b72';
      case 'year': return '#28a745';
      case 'permanent': return '#ffd700';
      default: return '#fff';
    }
  }};
`;

const CodeList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
`;

const CodeItem = styled.span`
  padding: 8px 12px;
  background: rgba(255,255,255,0.1);
  border-radius: 6px;
  color: #00ff88;
  font-family: monospace;
  font-size: 13px;
`;

const CodeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 10px;
  padding: 10px;
  background: rgba(255,255,255,0.02);
  border-radius: 8px;
`;

const API_BASE_URL = '/api/v1';

const AdminPage = () => {
  const { user, token: authToken } = useAuth();
  const token = authToken || localStorage.getItem('token');
  
  const [activeTab, setActiveTab] = useState('codes');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const [codeForm, setCodeForm] = useState({
    code_type: 'day',
    duration_days: 1,
    max_users: 1,
    quantity: 1
  });

  const [codes, setCodes] = useState([]);
  const [members, setMembers] = useState([]);
  const [generatedCodes, setGeneratedCodes] = useState([]);

  useEffect(() => {
    if (user?.is_admin === 1) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    const storedToken = localStorage.getItem('token');
    if (!storedToken) return;
    try {
      const headers = { Authorization: `Bearer ${storedToken}` };
      
      const [codesRes, membersRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/admin/codes/list`, { headers }),
        axios.get(`${API_BASE_URL}/admin/codes/members`, { headers })
      ]);

      if (codesRes.data.code === 1) {
        setCodes(codesRes.data.data || []);
      }
      if (membersRes.data.code === 1) {
        setMembers(membersRes.data.data || []);
      }
    } catch (error) {
      console.error('加载数据失败:', error);
    }
  };

  const handleGenerateCodes = async () => {
    const storedToken = localStorage.getItem('token');
    if (!storedToken) {
      setMessage({ type: 'error', text: '请先登录' });
      return;
    }
    setLoading(true);
    setMessage(null);

    try {
      const headers = { Authorization: `Bearer ${storedToken}` };
      const requestData = {
        code_type: codeForm.code_type,
        duration_days: codeForm.duration_days || 1,
        max_users: codeForm.max_users || 1,
        quantity: codeForm.quantity || 1
      };
      console.log('Sending request:', requestData);
      const response = await axios.post(`${API_BASE_URL}/admin/codes/generate`, requestData, { headers });
      console.log('Response:', response.data);

      if (response.data.code === 1) {
        setMessage({ type: 'success', text: response.data.msg });
        setGeneratedCodes(response.data.data.codes || []);
        loadData();
      } else {
        setMessage({ type: 'error', text: response.data.msg || '生成失败' });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || '生成失败' });
    } finally {
      setLoading(false);
    }
  };

  const getTypeName = (type) => {
    const names = {
      'day': '日卡',
      'week': '周卡',
      'season': '季卡',
      'year': '年卡',
      'permanent': '永久'
    };
    return names[type] || type;
  };

  const getMemberLevelName = (level) => {
    const names = {
      'free': '免费用户',
      'day': '日卡会员',
      'week': '周卡会员',
      'season': '季卡会员',
      'year': '年卡会员',
      'permanent': '永久会员'
    };
    return names[level] || level;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(parseInt(timestamp) * 1000).toLocaleString('zh-CN');
  };

  if (user?.is_admin !== 1) {
    return (
      <PageContainer>
        <Card>
          <h2 style={{color: '#fff', textAlign: 'center'}}>权限不足</h2>
          <p style={{color: 'rgba(255,255,255,0.6)', textAlign: 'center'}}>只有管理员可以访问此页面</p>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header>
        <Title>管理后台</Title>
      </Header>

      <TabContainer>
        <Tab active={activeTab === 'codes'} onClick={() => setActiveTab('codes')}>
          激活码管理
        </Tab>
        <Tab active={activeTab === 'members'} onClick={() => setActiveTab('members')}>
          会员管理
        </Tab>
      </TabContainer>

      {message && (
        <Card style={{
          background: message.type === 'success' ? 'rgba(0,255,136,0.1)' : 'rgba(255,0,0,0.1)',
          borderColor: message.type === 'success' ? 'rgba(0,255,136,0.3)' : 'rgba(255,0,0,0.3)'
        }}>
          <p style={{color: message.type === 'success' ? '#00ff88' : '#ff4444', margin: 0}}>{message.text}</p>
        </Card>
      )}

      {activeTab === 'codes' && (
        <>
          <Card>
            <h3 style={{color: '#fff', marginTop: 0}}>生成激活码</h3>
            
            <FormGroup>
              <Label>卡类型</Label>
              <Select 
                value={codeForm.code_type}
                onChange={(e) => setCodeForm({...codeForm, code_type: e.target.value})}
              >
                <option value="day">日卡 (1天)</option>
                <option value="week">周卡 (7天)</option>
                <option value="season">季卡 (90天)</option>
                <option value="year">年卡 (365天)</option>
                <option value="permanent">永久</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>最大使用次数</Label>
              <Input 
                type="number" 
                min="1"
                value={codeForm.max_users}
                onChange={(e) => setCodeForm({...codeForm, max_users: parseInt(e.target.value) || 1})}
              />
            </FormGroup>

            <FormGroup>
              <Label>生成数量</Label>
              <Input 
                type="number" 
                min="1"
                max="100"
                value={codeForm.quantity}
                onChange={(e) => setCodeForm({...codeForm, quantity: parseInt(e.target.value) || 1})}
              />
            </FormGroup>

            <Button onClick={handleGenerateCodes} disabled={loading}>
              {loading ? '生成中...' : '生成激活码'}
            </Button>

            {generatedCodes.length > 0 && (
              <>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20}}>
                  <Label style={{margin: 0}}>生成的激活码（{generatedCodes.length}个）：</Label>
                  <Button 
                    onClick={() => {
                      const content = generatedCodes.join('\n');
                      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `激活码_${codeForm.code_type}_${new Date().getTime()}.txt`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                    style={{padding: '8px 16px', fontSize: '12px'}}
                  >
                    导出TXT
                  </Button>
                </div>
                <div style={{marginTop: 15}}>
                  {Array.from({ length: Math.ceil(generatedCodes.length / 10) }, (_, rowIndex) => (
                    <CodeRow key={rowIndex}>
                      {generatedCodes.slice(rowIndex * 10, rowIndex * 10 + 10).map((code, idx) => (
                        <CodeItem key={rowIndex * 10 + idx}>{code}</CodeItem>
                      ))}
                    </CodeRow>
                  ))}
                </div>
              </>
            )}
          </Card>

          <Card>
            <h3 style={{color: '#fff', marginTop: 0}}>激活码列表</h3>
            <Table>
              <thead>
                <tr>
                  <Th>激活码</Th>
                  <Th>类型</Th>
                  <Th>天数</Th>
                  <Th>已用/总量</Th>
                  <Th>创建时间</Th>
                  <Th>过期时间</Th>
                </tr>
              </thead>
              <tbody>
                {codes.map((code) => (
                  <tr key={code.id}>
                    <Td style={{fontFamily: 'monospace', color: '#00ff88'}}>{code.code}</Td>
                    <Td><Badge type={code.code_type}>{getTypeName(code.code_type)}</Badge></Td>
                    <Td>{code.duration_days === 0 ? '永久' : code.duration_days + '天'}</Td>
                    <Td>{code.used_count}/{code.max_users}</Td>
                    <Td>{formatDate(code.created_at)}</Td>
                    <Td>{code.expires_at ? formatDate(code.expires_at) : '永久'}</Td>
                  </tr>
                ))}
                {codes.length === 0 && (
                  <tr>
                    <Td colSpan="6" style={{textAlign: 'center', color: 'rgba(255,255,255,0.5)'}}>
                      暂无数据
                    </Td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card>
        </>
      )}

      {activeTab === 'members' && (
        <Card>
          <h3 style={{color: '#fff', marginTop: 0}}>会员列表</h3>
          <Table>
            <thead>
              <tr>
                <Th>ID</Th>
                <Th>用户名</Th>
                <Th>邮箱</Th>
                <Th>会员等级</Th>
                <Th>过期时间</Th>
                <Th>状态</Th>
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={member.id}>
                  <Td>{member.user_id}</Td>
                  <Td>{member.username || '-'}</Td>
                  <Td>{member.email || '-'}</Td>
                  <Td><Badge type={member.member_level}>{getMemberLevelName(member.member_level)}</Badge></Td>
                  <Td>{member.expire_date ? formatDate(member.expire_date) : '永久'}</Td>
                  <Td style={{color: member.is_active === 1 ? '#00ff88' : '#ff4444'}}>
                    {member.is_active === 1 ? '正常' : '禁用'}
                  </Td>
                </tr>
              ))}
              {members.length === 0 && (
                <tr>
                  <Td colSpan="6" style={{textAlign: 'center', color: 'rgba(255,255,255,0.5)'}}>
                    暂无数据
                  </Td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card>
      )}
    </PageContainer>
  );
};

export default AdminPage;
